import "./ToolbarStyles.scss";

import type { PropsWithChildren } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import Collapse from "react-bootstrap/Collapse";
import { useResizeObserver } from "usehooks-ts";

import {
  BoldIcon,
  CircleArrowButton,
  CodeIcon,
  ItalicIcon,
  ListBulletsIcon,
  ListNumberedIcon,
  QuoteIcon,
  RedoIcon,
  UndoIcon,
} from "../../icons";
import { renderTooltip } from "../../StyledTooltip/StyledTooltip";
import { LeafTypes, NodeTypes } from "../slateTypes";
import { BlockButton } from "./BlockButton";
import { HistoryButton } from "./HistoryButton";
import { LinkButton } from "./LinkButton";
import { ListButton } from "./ListButton";
import { MarkButton } from "./MarkButton";
import { SelectBlockMode } from "./SelectBlockMode";

export interface BaseProps {
  hasChanges: boolean;
  inPopup?: boolean;
  withBanner?: boolean;
  label?: string;
}

export const Toolbar = ({
  hasChanges,
  inPopup,
  withBanner,
  label,
}: PropsWithChildren<BaseProps>) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<
    "desktop" | "middle" | "mobile"
  >("desktop");
  const { width = 0 } = useResizeObserver({
    ref,
    box: "border-box",
  });

  const [needAbsolute, setNeedAbsolute] = useState(false);
  const [showBottomTooltip, setShowBottomTooltip] = useState(false);
  const scrolledComponent = inPopup
    ? document.querySelector(".custom-modal")
    : document;

  const handleScroll = () => {
    const container = document.querySelector(".editor-wrapper");
    if (container) {
      const containerTop = container.getBoundingClientRect().top;
      setNeedAbsolute(containerTop <= 0);
      setShowBottomTooltip(containerTop <= 120);
    }
  };

  useEffect(() => {
    scrolledComponent &&
      scrolledComponent.addEventListener("scroll", handleScroll);
    return () => {
      scrolledComponent &&
        scrolledComponent.removeEventListener("scroll", handleScroll);
    };
  }, [scrolledComponent]);

  useEffect(() => {
    if (width < 440) {
      setContainerWidth("mobile");
    } else if (width < 650) {
      setContainerWidth("middle");
    } else {
      setContainerWidth("desktop");
    }
  }, [width]);

  const toggleCollapse = () => {
    setOpen(!open);
  };

  const collapseToolbar = useMemo(
    () => (
      <>
        <OverlayTrigger
          placement={showBottomTooltip ? "bottom" : "top"}
          overlay={renderTooltip(<span>Unordered list</span>)}
        >
          <span>
            <ListButton format={NodeTypes.UNORDERED_LIST}>
              <ListBulletsIcon />
            </ListButton>
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement={showBottomTooltip ? "bottom" : "top"}
          overlay={renderTooltip(<span>Ordered list</span>)}
        >
          <span>
            <ListButton format={NodeTypes.ORDERED_LIST}>
              <ListNumberedIcon />
            </ListButton>
          </span>
        </OverlayTrigger>
        <div className="editor-toolbar__divider" />
        <OverlayTrigger
          placement={showBottomTooltip ? "bottom" : "top"}
          overlay={renderTooltip(<span>Create link</span>)}
        >
          <span>
            <LinkButton />
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement={showBottomTooltip ? "bottom" : "top"}
          overlay={renderTooltip(<span>Block code</span>)}
        >
          <span>
            <BlockButton format={NodeTypes.BLOCK_CODE}>
              <CodeIcon />
            </BlockButton>
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement={showBottomTooltip ? "bottom" : "top"}
          overlay={renderTooltip(<span>Quote</span>)}
        >
          <span>
            <BlockButton format={NodeTypes.BLOCK_QUOTE}>
              <QuoteIcon />
            </BlockButton>
          </span>
        </OverlayTrigger>
      </>
    ),
    [showBottomTooltip],
  );
  return (
    <div
      className={`editor-toolbar-container ${inPopup ? "stick-to-top" : ""} ${
        withBanner ? "with-banner" : ""
      }`}
      data-testid="toolbar"
    >
      {label && (
        <label className="editor-toolbar__label d-block pt-7 fs-7 required form-label mb-0">
          {label}
        </label>
      )}
      <div ref={ref} className="editor-toolbar">
        {containerWidth !== "mobile" && (
          <div className="d-flex align-items-center ">
            <OverlayTrigger
              placement={showBottomTooltip ? "bottom" : "top"}
              overlay={renderTooltip(<span>Undo</span>)}
            >
              <span>
                <HistoryButton direction="undos" hasChanges={hasChanges}>
                  <UndoIcon />
                </HistoryButton>
              </span>
            </OverlayTrigger>
            <OverlayTrigger
              placement={showBottomTooltip ? "bottom" : "top"}
              overlay={renderTooltip(<span>Redo</span>)}
            >
              <span>
                <HistoryButton direction="redos" hasChanges={hasChanges}>
                  <RedoIcon />
                </HistoryButton>
              </span>
            </OverlayTrigger>
            <div className="editor-toolbar__divider" />
          </div>
        )}
        <OverlayTrigger
          placement={showBottomTooltip ? "bottom" : "top"}
          overlay={renderTooltip(<span>Block mode</span>)}
        >
          <div
            className={`editor-toolbar__select-wrapper ${
              containerWidth === "mobile" ? "ms-0" : ""
            }`}
          >
            <SelectBlockMode />
          </div>
        </OverlayTrigger>
        <OverlayTrigger
          placement={showBottomTooltip ? "bottom" : "top"}
          overlay={renderTooltip(<span>Bold</span>)}
        >
          <span>
            <MarkButton format={LeafTypes.BOLD}>
              <BoldIcon />
            </MarkButton>
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement={showBottomTooltip ? "bottom" : "top"}
          overlay={renderTooltip(<span>Italic</span>)}
        >
          <span>
            <MarkButton format={LeafTypes.ITALIC}>
              <ItalicIcon />
            </MarkButton>
          </span>
        </OverlayTrigger>
        {containerWidth === "desktop" && (
          <div className="d-flex align-items-center ">
            <div className="editor-toolbar__divider" />
            {collapseToolbar}
          </div>
        )}
        {containerWidth !== "desktop" && (
          <div className="d-flex align-items-center">
            <div className="editor-toolbar__divider" />
            <button
              data-testid="collapse-button"
              className="editor-toolbar-collapse-button"
              onClick={toggleCollapse}
            >
              <div
                className={`editor-toolbar__collapse-icon ${
                  open ? "opened" : "closed"
                }`}
              >
                <CircleArrowButton />
              </div>
            </button>
          </div>
        )}
      </div>
      {containerWidth !== "desktop" && (
        <Collapse
          in={open}
          className={`editor-toolbar-collapse ${
            needAbsolute ? "position-absolute w-100" : ""
          } `}
        >
          <div>
            <div className="d-flex align-items-center pb-3">
              {collapseToolbar}
            </div>
          </div>
        </Collapse>
      )}
    </div>
  );
};
